import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useAuth } from '@/lib/auth';
import { useSubscriptionContext } from '@/contexts/SubscriptionContext';
import { supabase } from '@/lib/supabase';
import { createCheckoutSession } from '@/lib/subscription';
import { toast } from '@/hooks/use-toast';
import { AlertTriangle, CreditCard, RefreshCw, Clock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface SubscriptionRenewalProps {
  className?: string;
}

export function SubscriptionRenewal({ className }: SubscriptionRenewalProps) {
  const { user } = useAuth();
  const subscription = useSubscriptionContext();
  const [isRenewing, setIsRenewing] = useState(false);

  // Check if this is a trial or free user.
  // NOTE: deliberately reads Stripe state directly (not useEffectiveJdsAccess)
  // because this component is specifically about *Stripe subscription* renewal.
  // bar_package users have no Stripe sub to renew — they should hit the
  // `!isActive` branch and the component returns null at line ~99.
  const isTrial = subscription.status === 'trialing' || subscription.tierName === 'Trial';
  const isFreeTier = !subscription.isActive || subscription.tierName === 'Free';
  
  // Calculate subscription status
  const isExpired = subscription.current_period_end && new Date(subscription.current_period_end) < new Date();
  const isExpiringSoon = subscription.current_period_end && 
    new Date(subscription.current_period_end) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) &&
    !isExpired;

  // Check if this is a manual subscription
  const isManualSubscription = subscription.stripe_price_id?.includes('manual_activation_') || false;

  const handleRenewal = async () => {
    if (!user) return;

    setIsRenewing(true);
    try {
      if (isManualSubscription) {
        // For manual subscriptions, use the renewal function
        const { data, error } = await supabase.rpc('renew_manual_subscription', {
          p_user_id: user.id,
          p_price_id: subscription.stripe_price_id,
          p_days: 30
        });

        if (error) throw error;

        const result = data as { success?: boolean; error?: string } | null;
        
        if (result?.success) {
          toast({
            title: 'Subscription Renewed',
            description: 'Your subscription has been extended for another 30 days.',
          });
          
          // Refresh subscription data
          await subscription.refreshSubscription();
        } else {
          throw new Error(result?.error || 'Failed to renew subscription');
        }
      } else {
        // For Stripe subscriptions, redirect to checkout
        // Use the existing subscription tier or default to unlimited
        const tier = subscription.tierName?.toLowerCase() || 'unlimited';
        const checkoutUrl = await createCheckoutSession(user.id, tier);
        
        if (checkoutUrl) {
          // The create-checkout-session endpoint returns a proper URL
          window.location.href = checkoutUrl;
        } else {
          throw new Error('Failed to create checkout session');
        }
      }
    } catch (error) {
      console.error('Error renewing subscription:', error);
      toast({
        title: 'Renewal Failed',
        description: error instanceof Error ? error.message : 'Failed to renew subscription',
        variant: 'destructive',
      });
    }
    setIsRenewing(false);
  };

  const formatRemainingTime = () => {
    if (!subscription.current_period_end) return '';
    
    const endDate = new Date(subscription.current_period_end);
    if (isExpired) {
      return `Expired ${formatDistanceToNow(endDate, { addSuffix: true })}`;
    }
    return `Expires ${formatDistanceToNow(endDate, { addSuffix: true })}`;
  };

  // Don't show anything for trial users, free tier users, or if subscription is active and not expiring soon
  if (isTrial || isFreeTier || (!isExpired && !isExpiringSoon)) {
    return null;
  }

  return (
    <Alert className={`${isExpired ? 'border-red-500' : 'border-blue-500'} ${className}`}>
      <AlertTriangle className={`h-4 w-4 ${isExpired ? 'text-red-500' : 'text-blue-500'}`} />
      <AlertTitle>
        {isExpired ? 'Subscription Expired' : 'Subscription Renewal Reminder'}
      </AlertTitle>
      <AlertDescription className="mt-2">
        <p className="mb-3">
          {isExpired
            ? 'Your subscription has expired. Renew now to regain access to premium features.'
            : isManualSubscription
              ? `Your subscription will expire soon. Renew now to ensure uninterrupted access.`
              : `Your subscription will automatically renew soon. No action needed!`
          }
        </p>
        <p className="text-sm text-muted-foreground mb-4 flex items-center gap-2">
          <Clock className="h-4 w-4" />
          {formatRemainingTime()}
        </p>
        {/* Only show button for expired subscriptions or manual subscriptions */}
        {(isExpired || isManualSubscription) && (
          <Button
            onClick={handleRenewal}
            disabled={isRenewing}
            className="w-full sm:w-auto"
            variant={isExpired ? 'destructive' : 'default'}
          >
            {isRenewing ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4 mr-2" />
                Renew Subscription
              </>
            )}
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
}

// Subscription status card for settings page
export function SubscriptionStatusCard() {
  const subscription = useSubscriptionContext();
  const isManualSubscription = subscription.stripe_price_id?.includes('manual_activation_') || false;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Subscription Details</CardTitle>
        <CardDescription>
          {subscription.tierName} Tier {isManualSubscription && '(Manual)'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Status</dt>
            <dd className="text-sm">
              {subscription.isActive ? (
                <span className="text-green-600">Active</span>
              ) : (
                <span className="text-red-600">Expired</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Renewal Date</dt>
            <dd className="text-sm">
              {subscription.current_period_end 
                ? new Date(subscription.current_period_end).toLocaleDateString()
                : 'N/A'
              }
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Auto-Renew</dt>
            <dd className="text-sm">
              {isManualSubscription ? 'No (Manual)' : 'Yes (Stripe)'}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Price ID</dt>
            <dd className="text-sm font-mono text-xs truncate">
              {subscription.stripe_price_id || 'N/A'}
            </dd>
          </div>
        </dl>
      </CardContent>
      <CardFooter>
        <SubscriptionRenewal className="w-full" />
      </CardFooter>
    </Card>
  );
} 